import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import CheckboxSelectItem from 'components/checkbox-select-item';
import FilterSidebar from 'components/filter-sidebar';
import Select from 'components/select';
import { FishGroup, Summary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { ScenarioResultFiltersType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-results-filters.type';
import {
  kpis,
  SELECT_ALL_KPI_ID,
  ALL_FISH_GROUP_ID,
  getKpiNameMessageId,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/compare-result-utils';
import {
  isLastConcreteSelection,
  resolveFishGroupSelection,
  toggleSelectionWithSelectAll,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/scenario-result-filters-utils';

type ScenarioResultFiltersProps = {
  feedPrograms: Summary[];
  fishGroups: FishGroup[];
  filters: ScenarioResultFiltersType;
  clearFilters: () => void;
  updateFilters: (newFilters: ScenarioResultFiltersType) => void;
  updateImmediateFilters: (newFilters: Partial<ScenarioResultFiltersType>) => void;
  handleApplyFilters: () => void;
};

const ScenarioResultFilters: React.FC<ScenarioResultFiltersProps> = ({
  feedPrograms,
  fishGroups,
  filters,
  clearFilters,
  updateFilters,
  updateImmediateFilters,
  handleApplyFilters,
}) => {
  const intl = useIntl();
  const allFeedProgramKeys = feedPrograms.map((program) => program.id);
  const allKpiKeys = kpis.filter((kpi) => kpi.id !== SELECT_ALL_KPI_ID).map((k) => k.id);
  const allFishGroupKeys = fishGroups.filter((fishGroup) => fishGroup.id !== ALL_FISH_GROUP_ID).map((fishGroup) => fishGroup.id);
  const allFeedProgramsSelected = allFeedProgramKeys.every((key) => filters.pendingFeedPrograms.includes(key));
  const fishGroupSelectValue = filters.pendingResultsFor.includes(ALL_FISH_GROUP_ID)
    ? [ALL_FISH_GROUP_ID, ...allFishGroupKeys]
    : filters.pendingResultsFor;
  const selectedFishGroupsCount = filters.pendingResultsFor.includes(ALL_FISH_GROUP_ID)
    ? allFishGroupKeys.length
    : filters.pendingResultsFor.length;

  return (
    <Box display={'flex'} flexDirection={'row'} gap={4} alignItems={'center'}>
      <FilterSidebar
        clearFilters={() => {
          clearFilters();
        }}>
        <Select
          multiple
          value={filters.pendingFeedPrograms}
          renderValue={() =>
            `${filters.pendingFeedPrograms.filter((program) => program !== 'all').length} of ${feedPrograms.length} programs`
          }
          label={intl.formatMessage({ id: 'bluViuNutritionR2.results.graph.showFeedProgram' })}
          onChange={(e) => {
            const newValue = e.target.value as string[];
            updateImmediateFilters({
              pendingFeedPrograms: toggleSelectionWithSelectAll(newValue, filters.pendingFeedPrograms, allFeedProgramKeys),
            });
          }}>
          <CheckboxSelectItem
            key={'all-feed-programs'}
            checked={filters.pendingFeedPrograms.includes('all') || allFeedProgramsSelected}
            value={'all'}
            divider>
            {intl.formatMessage({ id: 'general.selectAll' })}
          </CheckboxSelectItem>
          {feedPrograms.map((program) => (
            <CheckboxSelectItem
              key={program.id}
              checked={filters.pendingFeedPrograms.includes(program.id)}
              value={program.id}
              disabled={isLastConcreteSelection(filters.pendingFeedPrograms, program.id, 'all')}>
              {program.feedPlan}
            </CheckboxSelectItem>
          ))}
        </Select>

        <Select
          multiple
          value={filters.pendingKPIs}
          renderValue={() =>
            `${filters.pendingKPIs.filter((k) => k !== SELECT_ALL_KPI_ID).length} of ${allKpiKeys.length} KPIs groups`
          }
          label={intl.formatMessage({ id: 'bluViuNutrition.viewScenarioResultGt2.filters.visibleKpis' })}
          onChange={(e) => {
            const newValue = e.target.value as string[];
            updateImmediateFilters({
              pendingKPIs: toggleSelectionWithSelectAll(newValue, filters.pendingKPIs, allKpiKeys, SELECT_ALL_KPI_ID),
            });
          }}>
          {kpis.map((kpi, index) => (
            <CheckboxSelectItem
              key={kpi.id}
              checked={
                kpi.id === SELECT_ALL_KPI_ID
                  ? filters.pendingKPIs.includes(SELECT_ALL_KPI_ID) || allKpiKeys.every((k) => filters.pendingKPIs.includes(k))
                  : filters.pendingKPIs.includes(kpi.id)
              }
              value={kpi.id}
              divider={index === 0}
              disabled={
                kpi.id !== SELECT_ALL_KPI_ID && isLastConcreteSelection(filters.pendingKPIs, kpi.id, SELECT_ALL_KPI_ID)
              }>
              {intl.formatMessage({ id: getKpiNameMessageId(kpi.id) })}
            </CheckboxSelectItem>
          ))}
        </Select>
        <Select
          multiple
          value={fishGroupSelectValue}
          label={intl.formatMessage({ id: 'bluViuNutritionR2.results.graph.showResultsFor' })}
          renderValue={() => `${selectedFishGroupsCount} of ${allFishGroupKeys.length} fish groups`}
          onChange={(e) => {
            const newValue = e.target.value as string[];
            updateImmediateFilters({
              pendingResultsFor: resolveFishGroupSelection(
                toggleSelectionWithSelectAll(
                  newValue,
                  filters.pendingResultsFor,
                  allFishGroupKeys,
                  ALL_FISH_GROUP_ID
                ),
                filters.pendingResultsFor
              ),
            });
          }}>
          {fishGroups.map((fishGroup, index) => {
            const allFishIds = fishGroups.filter((f) => f.id !== ALL_FISH_GROUP_ID).map((f) => f.id);
            return (
              <CheckboxSelectItem
                key={fishGroup.id}
                value={fishGroup.id}
                divider={index === 0}
                checked={
                  fishGroup.id === ALL_FISH_GROUP_ID
                    ? filters.pendingResultsFor.includes(ALL_FISH_GROUP_ID) ||
                    allFishIds.every((id) =>
                      filters.pendingResultsFor.includes(id)
                    )
                    : filters.pendingResultsFor.includes(ALL_FISH_GROUP_ID) ||
                    filters.pendingResultsFor.includes(fishGroup.id)
                }
                disabled={
                  fishGroup.id !== ALL_FISH_GROUP_ID &&
                  isLastConcreteSelection(filters.pendingResultsFor, fishGroup.id, ALL_FISH_GROUP_ID)
                }>
                {fishGroup.name}
              </CheckboxSelectItem>
            );
          })}
        </Select>
      </FilterSidebar>
    </Box>
  );
};

export default ScenarioResultFilters;
